import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select'
import { Banner, Button } from 'slab'
import {
  getGetBrokersTasksQueryKey,
  getGetBrokersTasksTaskIdQueryKey,
  getGetBrokersTaskWorkflowsQueryKey,
  usePatchBrokersTasks,
} from 'utils/web_api'
import type {
  BrokersTask,
  BrokersTaskBlockedReason,
} from 'utils/web_api.schemas'
import { invalidateWorkflowQueries } from 'utils/workflowCacheUtils'

interface BlockTaskDialogProps {
  task: BrokersTask
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  workflowId?: string
  enrollmentId?: string
  memberId?: string
}

// Type-safe label mapping - TypeScript will error if any enum value is missing or extra
export const BLOCKED_REASON_LABELS: Record<
  BrokersTaskBlockedReason,
  string
> = {
  general_agent: 'General Agent',
  member: 'Member',
  carrier: 'Carrier',
  missing_info: 'Missing Information',
  incident: 'Incident',
  other: 'Other',
}

// Convert to options format
const BLOCKED_BY_OPTIONS = Object.entries(BLOCKED_REASON_LABELS).map(
  ([value, label]) => ({
    value,
    label,
  })
)

export const BlockTaskDialog = ({
  task,
  isOpen,
  onClose,
  onSuccess,
  workflowId,
  enrollmentId,
  memberId,
}: BlockTaskDialogProps) => {
  const [blockedReason, setBlockedReason] = useState<string>('')
  const [blockedReasonMessage, setBlockedReasonMessage] = useState<string>('')
  const queryClient = useQueryClient()

  const blockMutation = usePatchBrokersTasks()

  const handleBlock = () => {
    if (!blockedReason) {
      toast.error('Please select a blocker category')
      return
    }

    if (!blockedReasonMessage.trim()) {
      toast.error('Please provide a reason for blocking')
      return
    }

    const message = `Blocked: ${blockedReasonMessage.trim()}`

    blockMutation.mutate(
      {
        data: {
          task_ids: [task.id],
          status_event: 'block',
          status_message: message,
          blocked_reason: blockedReason as BrokersTaskBlockedReason,
        },
      },
      {
        onSuccess: () => {
          // Invalidate all task-related queries to refresh the UI
          queryClient.invalidateQueries({
            queryKey: getGetBrokersTasksQueryKey(),
          })
          queryClient.invalidateQueries({
            queryKey: getGetBrokersTasksTaskIdQueryKey(task.id),
          })
          queryClient.invalidateQueries({
            queryKey: getGetBrokersTaskWorkflowsQueryKey(),
          })

          // Invalidate all workflow-related queries using centralized utility
          invalidateWorkflowQueries(queryClient, {
            workflowId,
            enrollmentId,
            memberId,
            task,
          })

          toast.success('Task blocked successfully')
          onSuccess?.()
          handleClose()
        },
        onError: (error) => {
          console.error('Failed to block task:', error)

          // Try to extract error message from the response
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to block task. Please try again.'

          toast.error(errorMessage)

          // Invalidate queries anyway in case the task was actually updated
          // (handles the case where the server updated the task but returned an error)
          queryClient.invalidateQueries({
            queryKey: getGetBrokersTasksQueryKey(),
          })
          queryClient.invalidateQueries({
            queryKey: getGetBrokersTasksTaskIdQueryKey(task.id),
          })

          // Also invalidate workflow queries if available
          invalidateWorkflowQueries(queryClient, {
            workflowId,
            enrollmentId,
            memberId,
            task,
          })
        },
      }
    )
  }

  const handleClose = () => {
    setBlockedReason('')
    setBlockedReasonMessage('')
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle>Block Task</DialogTitle>
          <DialogDescription>
            Mark this task as blocked if there is an external reason it cannot be completed that's
            not represented by the current set of workflow tasks.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-4'>
          <div className='space-y-2'>
            <Label htmlFor='blocked-by'>Blocked By *</Label>
            <Select value={blockedReason} onValueChange={setBlockedReason}>
              <SelectTrigger id='blocked-by'>
                <SelectValue placeholder='Select blocker category' />
              </SelectTrigger>
              <SelectContent>
                {BLOCKED_BY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className='text-sm text-gray-500'>
              Who or what is causing the block?
            </p>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='blocked-reason'>Reason *</Label>
            <Input
              id='blocked-reason'
              value={blockedReasonMessage}
              onChange={(e) => setBlockedReasonMessage(e.target.value)}
              placeholder='E.g., Awaiting member signature on consent form'
              className='w-full'
            />
            <p className='text-sm text-gray-500'>
              Provide specific details about why this task is blocked
            </p>
          </div>

          <Banner
            title='Blocked tasks must be manually unblocked'
            type='notice'
          >
            The task will be marked as blocked and, if automated, will not be
            resumed until manually unblocked. Please be sure to address
            unblocked tasks or escalate to a team member to ensure this task is
            properly processed.
          </Banner>
        </div>

        <DialogFooter>
          <Button
            variant='secondary'
            onClick={handleClose}
            disabled={blockMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={handleBlock}
            disabled={
              blockMutation.isPending || !blockedReason || !blockedReasonMessage.trim()
            }
          >
            {blockMutation.isPending ? 'Blocking...' : 'Block Task'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
